import { createRoutesFromChildren, useRoutes } from "react-router-dom";
import type { RouteObject, RoutesProps } from "react-router-dom";

export interface HostedSite {
    hostnames: readonly string[];
    routePrefix: string;
    name: string;
}

function normalizeHostname(hostname: string): string {
    return hostname.toLowerCase().replace(/\.$/, "");
}

export function resolveHostedSite(hostname: string, sites: readonly HostedSite[]): HostedSite | undefined {
    const configuredHostnames = new Set<string>();
    let match: HostedSite | undefined;
    for (const site of sites) {
        if (!site.name.trim() || site.hostnames.length === 0) {
            throw new Error("Each hosted site must have a name and at least one hostname.");
        }
        if (!/^\/(?:[a-z0-9_-]+(?:\/[a-z0-9_-]+)*)?$/i.test(site.routePrefix)) {
            throw new Error(`Hosted site "${site.name}" must have a literal route prefix without a trailing slash, query, or fragment.`);
        }
        for (const configuredHostname of site.hostnames) {
            const normalized = normalizeHostname(configuredHostname);
            if (normalized.length > 253 || !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/.test(normalized)) {
                throw new Error(`Invalid hosted-site hostname "${configuredHostname}". Use an exact hostname without a scheme, port, path, or wildcard.`);
            }
            if (configuredHostnames.has(normalized)) {
                throw new Error(`Hosted-site hostname "${configuredHostname}" is configured more than once.`);
            }
            configuredHostnames.add(normalized);
            if (normalized === normalizeHostname(hostname)) {
                match = site;
            }
        }
    }
    return match;
}

export function getHostedPath(path: string, site?: HostedSite): string | undefined {
    if (!site || site.routePrefix === "/") {
        return path;
    }
    const suffixIndex = path.search(/[?#]/);
    const pathname = suffixIndex < 0 ? path : path.slice(0, suffixIndex);
    const suffix = suffixIndex < 0 ? "" : path.slice(suffixIndex);
    const prefix = site.routePrefix;
    if (pathname.toLowerCase() === prefix.toLowerCase()) {
        return "/" + suffix;
    }
    if (pathname.toLowerCase().startsWith(prefix.toLowerCase() + "/")) {
        return pathname.slice(prefix.length) + suffix;
    }
    return undefined;
}

function rebaseRoute(route: RouteObject, site: HostedSite): RouteObject {
    const path = route.path?.startsWith("/") ? getHostedPath(route.path, site) : route.path;
    if (route.path?.startsWith("/") && path === undefined) {
        throw new Error(`Child route "${route.path}" is outside hosted site "${site.routePrefix}".`);
    }
    if (route.children) {
        return { ...route, path, children: route.children.map(child => rebaseRoute(child, site)) };
    }
    return { ...route, path };
}

export function getHostedRoutes(routes: RouteObject[], site?: HostedSite, sharedPaths: readonly string[] = []): RouteObject[] {
    if (!site || site.routePrefix === "/") {
        return routes;
    }
    let hasSiteRoute = false;
    const hostedRoutes: RouteObject[] = [];
    for (const route of routes) {
        if (route.path === "*" || (route.path && sharedPaths.includes(route.path))) {
            hostedRoutes.push(route);
        } else if (route.path && getHostedPath(route.path, site) !== undefined) {
            hostedRoutes.push(rebaseRoute(route, site));
            hasSiteRoute = true;
        }
    }
    if (!hasSiteRoute) {
        throw new Error(`Hosted site "${site.name}" has no React routes under "${site.routePrefix}".`);
    }
    return hostedRoutes;
}

export function HostedRoutes(props: { children: RoutesProps["children"]; site?: HostedSite; sharedPaths?: readonly string[] }) {
    return useRoutes(getHostedRoutes(createRoutesFromChildren(props.children), props.site, props.sharedPaths));
}
